<template lang="pug">
  span.yz-badge(:class="badgeClasses" :style="badgeStyle" v-bind="$attrs" v-on="$listeners")
    slot
    yz-btn(v-if="closable" icon="mdi-close" icon-only, size="sm" @click.stop="onClose")
</template>

<script>
import {getParentClasses, hasPaddingClasses} from '@/utils'

export default {
  name: "YzBadge",
  props: {
    size: {
      type: String,
      default: 'sm',
      validator: value => ['sm', 'md'].includes(value)
    },
    color: {
      type: String,
      default: 'primary'
    },
    border: {
      type: Boolean,
      default: false
    },
    borderColor: {
      type: String,
      default: null
    },
    textColor: {
      type: String,
      default: 'neutral-500'
    },
    radius: {
      type: [String, Number],
      default: '6px'
    },
    height: {
      type: [String, Number],
      default: null
    },
    closable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    parentClass() {
      return getParentClasses(this.$vnode)
    },
    badgeClasses() {
      const classes = []

      // Size class
      classes.push(`yz-badge--${this.size}`)

      // Font type
      if (this.size === 'sm') {
        classes.push('yz-medium-body-xs')
      } else {
        classes.push('yz-medium-body-sm')
      }

      // Color class
      if (this.color === 'primary') {
        classes.push(`yz-badge--${this.color}`)
      } else if (this.color && this.color.startsWith('#')) {
        // Hex color will be handled in badgeStyle computed property
      } else {
        classes.push(`bg-${this.color}`)
      }

      // Border class
      if (this.border) {
        classes.push('yz-badge--bordered')
      }

      // Smart padding - check if parent has padding classes
      if (hasPaddingClasses(this.parentClass)) {
        //
      } else {
        // Add default padding (2px 4px for both sizes) plus any other parent classes
        classes.push(`yz-default-padding ${this.parentClass}`.trim())
      }

      return classes
    },
    badgeStyle() {
      const style = {}

      // Handle background color
      if (this.color && this.color.startsWith('#')) {
        style.backgroundColor = this.color
      }

      // Handle border color
      if (this.border && this.borderColor) {
        if (this.borderColor.startsWith('#')) {
          style.borderColor = this.borderColor
        } else if (this.borderColor.startsWith('var(')) {
          style.borderColor = this.borderColor
        } else if (this.borderColor.startsWith('yz-')) {
          style.borderColor = `var(--${this.borderColor})`
        } else {
          style.borderColor = `var(--yz-${this.borderColor})`
        }
      }

      // Handle text color
      if (this.textColor) {
        if (this.textColor.startsWith('#')) {
          style.color = this.textColor
        } else if (this.textColor.startsWith('yz-')) {
          style.color = `var(--${this.textColor})`
        } else {
          style.color = `var(--yz-${this.textColor})`
        }
      }

      // Handle custom height
      if (this.height !== null) {
        const heightValue = typeof this.height === 'number' ? `${this.height}px` : this.height
        style.height = heightValue
        style.minHeight = heightValue
      } else {
        // Handle custom padding when no custom height is provided
        if (hasPaddingClasses(this.parentClass)) {
          // Remove fixed height when custom padding is used for better alignment
          style.height = 'auto'
          style.minHeight = this.size === 'sm' ? '20px' : '24px'
        }
      }

      // Handle border radius
      style.borderRadius = typeof this.radius === 'number' ? `${this.radius}px` : this.radius

      return style
    }
  },
  methods: {
    onClose(event) {
      this.$emit('close', event)
    }
  }
}
</script>

<style scoped>
/* Base badge styles */
.yz-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 500;
  vertical-align: middle;
  white-space: nowrap;
  box-sizing: border-box;
}

.yz-default-padding {
  padding: 2px 4px;
}

/* Size variants */
.yz-badge--sm {
  height: 20px;
  font-size: 12px;
  line-height: 16px;
}

.yz-badge--md {
  height: 24px;
  font-size: 14px;
  line-height: 20px;
}

/* Color variants - Primary */
.yz-badge--primary {
  background-color: var(--yz-neutral-200);
}


/* Border variant */
.yz-badge--bordered {
  border: 1px solid var(--yz-neutral-400);
}

/* Default border color for bordered badges without explicit borderColor */
.yz-badge--bordered:not([style*="border-color"]) {
  border-color: var(--yz-neutral-400);
}
</style>